import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Sovereign's Summons (The Hollow Sovereign's signature) — the court
 * decides where you stand. A royal seal marks a spot near the player for
 * the whole telegraph, then the player is TELEPORTED onto it and must
 * immediately survive a two-beat punish: outward rings erupting from the
 * seal (each with a gap), then a fast aimed volley from the boss arriving
 * where you were just placed. The counterplay is the read: being mid-dash
 * on the summons beat slips the decree entirely — no teleport, no storm,
 * and the affronted Sovereign is left wide open (vulnerability window).
 */
export class SovereignsSummons extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: "Sovereign's Summons", telegraphDuration: 1.4, activeDuration: 1.6, minPhase });
    this.config = config;
  }

  onBegin(_boss, player) {
    // The seal lands a modest hop away — far enough that the yank matters,
    // near enough that the player can pre-read the destination.
    const angle = Math.random() * Math.PI * 2;
    const dist = 90 + Math.random() * 70;
    const margin = 30;
    this._sealX = Math.min(ARENA.x + ARENA.width - margin, Math.max(ARENA.x + margin, player.x + Math.cos(angle) * dist));
    this._sealY = Math.min(ARENA.y + ARENA.height - margin, Math.max(ARENA.y + margin, player.y + Math.sin(angle) * dist));
    this._slipped = false;
    this._volleyTimer = null;
  }

  onActiveStart(boss, player, pool) {
    if (player.isDashing || player.isInvincible) {
      // Slipped the decree — the whole punish is cancelled and the
      // Sovereign is exposed instead. This is the pattern's mastery reward.
      this._slipped = true;
      boss.openVulnerability(0.5, 1.4);
      return;
    }
    player.x = this._sealX;
    player.y = this._sealY;
    this._volleyTimer = 0.45;

    // Two rings erupt the moment the player lands — spawned already away
    // from the seal and moving outward, so the seal itself stays a safe
    // eye; they punish sprinting blindly away from the landing spot.
    for (let ring = 0; ring < 2; ring++) {
      const count = 12;
      const gapAt = Math.floor(Math.random() * count);
      for (let i = 0; i < count; i++) {
        if (i === gapAt || i === (gapAt + 1) % count) continue;
        const a = (i / count) * Math.PI * 2 + ring * 0.26;
        const r = 30 + ring * 22;
        pool.fire({
          x: this._sealX + Math.cos(a) * r,
          y: this._sealY + Math.sin(a) * r,
          vx: Math.cos(a) * this.config.ringSpeed,
          vy: Math.sin(a) * this.config.ringSpeed,
          radius: 2.5,
          damage: this.config.ringDamage,
          color: '#ffd166',
          lifetime: 3,
        });
      }
    }
  }

  onActiveTick(dt, boss, _player, pool) {
    if (this._slipped || this._volleyTimer === null) return;

    this._volleyTimer -= dt;
    if (this._volleyTimer <= 0) {
      this._volleyTimer = null;
      // The court fires on its own seal — the player has had 0.45s to
      // step off the landing spot.
      const aim = Math.atan2(this._sealY - boss.y, this._sealX - boss.x);
      for (let i = -1; i <= 1; i++) {
        const a = aim + i * 0.14;
        pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(a) * this.config.volleySpeed,
          vy: Math.sin(a) * this.config.volleySpeed,
          radius: 3,
          damage: this.config.volleyDamage,
          color: '#ff5d6c',
          lifetime: 2.5,
        });
      }
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return {
        kind: 'summonsSeal',
        x: this._sealX,
        y: this._sealY,
        fromX: boss.x,
        fromY: boss.y,
        t: 1 - this._timer / this.telegraphDuration,
        active: false,
      };
    }
    if (this.state === PatternState.ACTIVE && !this._slipped) {
      return { kind: 'summonsSeal', x: this._sealX, y: this._sealY, fromX: boss.x, fromY: boss.y, t: 1, active: true };
    }
    return null;
  }
}
